import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Text } from '@chakra-ui/react';
import {
  useLazyGetCostCenterQuery,
  useAppSelector,
  useAppDispatch,
  setCostCenters,
  useLazyMediationListQuery,
} from '@spartalabs/pdc-core';
import { Container, FowardHeader, Unauthorized } from 'components';
import TableCard from 'components/Cards/TableCard';
import { lastDayOfMonth } from 'date-fns';
import { useCustomToast } from 'hooks';
import { checkDateRange } from 'utils/functions';
export const FinancialList: React.FC = () => {
  const { groups, costCenters, currentGroupId } = useAppSelector(
    state => state.authSlicer.user,
  );

  const globalDate = useAppSelector(state => state.globalDataSlicer.globalDate);
  const permissions = useAppSelector(
    state => state.authSlicer.user.permissions,
  );

  const dispatch = useAppDispatch();
  const { handleToast } = useCustomToast();

  const [dateRange, setDateRange] = useState(
    checkDateRange([
      {
        ...globalDate[0],
        endDate: lastDayOfMonth(new Date(globalDate[0].endDate || new Date())),
      },
    ]),
  );

  const [fetchMediations, { data, isLoading, isFetching, isError }] =
    useLazyMediationListQuery();

  const [
    fetchCostCenters,
    {
      isLoading: loadingCenters,
      isFetching: fetchingCenters,
      isError: isErrorCenters,
    },
  ] = useLazyGetCostCenterQuery();

  const navigate = useNavigate();
  const { state } = useLocation();

  const handleNavigate = (
    id: string,
    selected?: { id: string; name: string } | null,
  ) => {
    navigate(`mediacao/${id}`, { state: { selected } });
  };

  const fetchCostCenter = useCallback(async () => {
    const groupsString = groups
      ?.map(item => `grupoId=${item.groupId}`)
      .join('&');

    try {
      if (groupsString) {
        const groups = await fetchCostCenters(groupsString).unwrap();
        dispatch(setCostCenters(groups));
      }
    } catch {
      handleToast({
        step: 4,
        action: 'obter os centros de custo',
      });
    }
  }, [groups, currentGroupId]);

  const selectOptions = useMemo(() => {
    return (
      costCenters
        ?.filter(center => center.groupId === currentGroupId)
        .flatMap(item => item.costCenters) || []
    );
  }, [costCenters, currentGroupId]);

  useEffect(() => {
    if (!costCenters) fetchCostCenter();
  }, []);

  useEffect(() => {
    window.history.replaceState({}, document.title);
  }, [globalDate]);

  const tableLabels: TableLabel[] = useMemo(
    () => [
      {
        title: 'Mês',
        type: 'text',
        accessor: 'month',
        disableSorting: true,
      },
      {
        title: 'Ação',
        type: 'custom',
        component: (id, selected) => (
          <Text
            as="button"
            textColor="text.100"
            textDecoration="underline"
            onClick={() => handleNavigate(id, selected)}
          >
            Ver detalhes
          </Text>
        ),
        accessor: 'id',
      },
      {
        title: 'Valor a faturar',
        type: 'text',
        accessor: 'totalValue',
        disableSorting: true,
      },
      {
        title: 'Período',
        type: 'number',
        accessor: 'date',
        disableSorting: true,
      },
      {
        title: 'Detalhes',
        type: 'tag',
        accessor: 'status',
      },
    ],
    [],
  );

  const handleRender = useMemo(
    () =>
      selectOptions.length > 0
        ? {
            fetchData: fetchMediations,
            refetch: fetchMediations,
            setDateRange: setDateRange,
          }
        : null,
    [selectOptions],
  );

  if (!permissions?.includes('finan')) return <Unauthorized />;

  return (
    <Container mt="0px" pt="0px">
      <FowardHeader title="Faturamento e medições" />

      <TableCard
        comeBackId={state?.selected}
        selectOptions={selectOptions}
        hasActions
        loading={isLoading || isFetching || loadingCenters || fetchingCenters}
        title="Faturamento"
        subtitle="Acompanhe o status dos seus faturamentos mensais"
        overflowX="auto"
        w="100%"
        labels={tableLabels}
        data={selectOptions.length > 0 ? data : { itens: [] }}
        isError={isError || isErrorCenters}
        initDate={dateRange}
        {...handleRender}
      />
    </Container>
  );
};
